import React, { useEffect, useRef, useCallback, useState } from "react";
import { SignInButton, useAuth } from "@clerk/clerk-react";
import { Check, Lock, X } from "lucide-react";
import "./PaywallModal.css";

const PLANS = [
    {
        id: "yearly",
        label: "Annual prepaid",
        monthlyEquivalent: "$33.25",
        detail: "($399 billed annually)",
        todayTotal: "$399.00",
        summaryLabel: "Annual subscription",
        lineItem: "$399.00",
        badge: "2 months free",
    },
    {
        id: "annual_monthly",
        label: "Annual billed monthly*",
        monthlyEquivalent: "$36.99",
        detail: "(12 payments, billed monthly)",
        todayTotal: "$36.99",
        summaryLabel: "Annual subscription",
        lineItem: "$36.99",
    },
    {
        id: "monthly",
        label: "Monthly",
        monthlyEquivalent: "$39.99",
        detail: "(billed monthly)",
        todayTotal: "$39.99",
        summaryLabel: "Monthly subscription",
        lineItem: "$39.99",
    },
];

/**
 * Wide checkout dialog with horizontal plan cards and an order summary.
 *
 * Props:
 *  - isVisible: boolean
 *  - onClose: () => void
 *  - onSubscribe: (plan: "yearly" | "annual_monthly" | "monthly", promotionCode?: string) => void
 *  - onValidatePromo?: (plan, promotionCode) => Promise<promoPreview>
 *  - isCheckoutLoading: boolean
 */
export default function PaywallModal({
    isVisible,
    onClose,
    onSubscribe,
    onValidatePromo,
    isCheckoutLoading,
}) {
    const { isSignedIn } = useAuth();
    const dialogRef = useRef(null);
    const previousFocusRef = useRef(null);
    const [selectedPlan, setSelectedPlan] = useState("yearly");
    const [promoCode, setPromoCode] = useState("");
    const [showPromoField, setShowPromoField] = useState(false);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoError, setPromoError] = useState("");
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);

    const trapFocus = useCallback((e) => {
        if (e.key === "Escape") {
            onClose();
            return;
        }
        if (e.key !== "Tab") return;
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = dialog.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }, [onClose]);

    useEffect(() => {
        if (!isVisible) return;
        previousFocusRef.current = document.activeElement;
        setSelectedPlan("yearly");
        setPromoCode("");
        setShowPromoField(false);
        setAppliedPromo(null);
        setPromoError("");
        setIsValidatingPromo(false);
        document.body.style.overflow = "hidden";
        const dialog = dialogRef.current;
        if (dialog) {
            const closeBtn = dialog.querySelector(".paywall-close");
            if (closeBtn) closeBtn.focus();
        }
        document.addEventListener("keydown", trapFocus);
        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", trapFocus);
            const previousFocus = previousFocusRef.current;
            if (previousFocus && document.contains(previousFocus) && typeof previousFocus.focus === "function") {
                previousFocus.focus();
            }
        };
    }, [isVisible, trapFocus]);

    const applyPromo = useCallback(async (planId, code) => {
        const cleaned = (code || "").trim();
        if (!cleaned) {
            setPromoError("Enter a promotion code.");
            return null;
        }
        if (typeof onValidatePromo !== "function") {
            setPromoError("Promotion codes are unavailable right now.");
            return null;
        }

        setIsValidatingPromo(true);
        setPromoError("");
        try {
            const preview = await onValidatePromo(planId, cleaned);
            setAppliedPromo(preview);
            setPromoCode(preview.code || cleaned.toUpperCase());
            setShowPromoField(true);
            return preview;
        } catch (err) {
            setAppliedPromo(null);
            setPromoError(err.message || "Invalid or expired promotion code.");
            return null;
        } finally {
            setIsValidatingPromo(false);
        }
    }, [onValidatePromo]);

    const selectPlan = useCallback((planId) => {
        setSelectedPlan(planId);
        if (appliedPromo?.code) {
            applyPromo(planId, appliedPromo.code);
        }
    }, [appliedPromo, applyPromo]);

    if (!isVisible) return null;

    const selected = PLANS.find((p) => p.id === selectedPlan) || PLANS[0];
    const hasActivePromo = Boolean(appliedPromo?.active && appliedPromo?.finalAmount);
    const displayTotal = hasActivePromo ? appliedPromo.finalAmount : selected.todayTotal;
    const busy = isCheckoutLoading || isValidatingPromo;

    const clearPromo = () => {
        setAppliedPromo(null);
        setPromoError("");
        setPromoCode("");
    };

    // Unsigned users: compact sign-in prompt (not the checkout layout)
    if (!isSignedIn) {
        return (
            <div
                className="paywall-overlay"
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="paywall-title"
                    className="paywall-dialog paywall-dialog--compact fade-in-up"
                >
                    <button type="button" className="paywall-close" onClick={onClose} aria-label="Close">
                        <X size={18} strokeWidth={2.25} />
                    </button>
                    <div className="paywall-icon" aria-hidden="true">
                        <Lock size={28} strokeWidth={2} />
                    </div>
                    <h3 id="paywall-title" className="paywall-title">Free Trial Complete</h3>
                    <p className="paywall-subtitle">
                        Please sign in or sign up to evaluate the application during your 1-day free trial.
                    </p>
                    <SignInButton mode="modal">
                        <button type="button" className="paywall-cta">Sign Up Free</button>
                    </SignInButton>
                    <button type="button" className="paywall-dismiss" onClick={onClose}>
                        Maybe later
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="paywall-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="paywall-title"
                className="paywall-dialog paywall-dialog--checkout fade-in-up"
            >
                <button type="button" className="paywall-close" onClick={onClose} aria-label="Close">
                    <X size={18} strokeWidth={2.25} />
                </button>

                <div className="paywall-checkout">
                    <div className="paywall-checkout-main">
                        <div className="paywall-brand">
                            <img
                                src={`${process.env.PUBLIC_URL}/revelo.png`}
                                alt="Revelo"
                                className="paywall-revelo-logo"
                            />
                            <h2 id="paywall-title" className="sr-only">
                                AcquisitionAI Optibot Pro checkout
                            </h2>
                        </div>

                        <section className="paywall-product-card" aria-labelledby="paywall-product-name">
                            <div className="paywall-product-header">
                                <h3 id="paywall-product-name" className="paywall-product-name">
                                    AcquisitionAI Optibot Pro
                                </h3>
                            </div>

                            <div className="paywall-cycle-block">
                                <p className="paywall-cycle-label">Subscription cycle</p>

                                <div
                                    className="paywall-plans-row"
                                    role="radiogroup"
                                    aria-label="Subscription cycle"
                                >
                                    {PLANS.map((plan) => {
                                        const isSelected = selectedPlan === plan.id;
                                        return (
                                            <button
                                                key={plan.id}
                                                type="button"
                                                role="radio"
                                                aria-checked={isSelected}
                                                className={`paywall-plan${isSelected ? " is-selected" : ""}${plan.badge ? " has-badge" : ""}`}
                                                onClick={() => selectPlan(plan.id)}
                                                disabled={busy}
                                            >
                                                {plan.badge && (
                                                    <span className="paywall-plan-badge">{plan.badge}</span>
                                                )}
                                                {isSelected && (
                                                    <span className="paywall-plan-check" aria-hidden="true">
                                                        <Check size={14} strokeWidth={3} />
                                                    </span>
                                                )}
                                                <span className="paywall-plan-label">{plan.label}</span>
                                                <span className="paywall-plan-price">
                                                    <span className="paywall-plan-amount">{plan.monthlyEquivalent}</span>
                                                    <span className="paywall-plan-unit"> / month</span>
                                                </span>
                                                <span className="paywall-plan-detail">{plan.detail}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <p className="paywall-footnote">
                                    * With Annual, billed monthly, all 12 monthly payments must be made in full, even if you cancel.
                                </p>
                            </div>
                        </section>
                    </div>

                    <aside className="paywall-summary-panel" aria-label="Order summary">
                        <h3 className="paywall-summary-heading">Order summary</h3>

                        <div className="paywall-summary-item">
                            <div className="paywall-summary-item-title">AcquisitionAI Optibot Pro</div>
                            <div className="paywall-summary-item-meta">{selected.summaryLabel}</div>
                            <div className="paywall-summary-item-line">
                                <span>{selected.lineItem}</span>
                                <span className={hasActivePromo ? "paywall-price-original" : undefined}>
                                    {hasActivePromo ? appliedPromo.originalAmount : selected.todayTotal}
                                </span>
                            </div>
                        </div>

                        {!showPromoField ? (
                            <button
                                type="button"
                                className="paywall-promo-toggle"
                                onClick={() => setShowPromoField(true)}
                                disabled={busy}
                            >
                                Have a promotion code?
                            </button>
                        ) : (
                            <div className="paywall-promo-field">
                                <label htmlFor="paywall-promo-code" className="paywall-promo-label">
                                    Promotion code
                                </label>
                                <div className="paywall-promo-row">
                                    <input
                                        id="paywall-promo-code"
                                        type="text"
                                        className="paywall-promo-input"
                                        value={promoCode}
                                        onChange={(e) => {
                                            setPromoCode(e.target.value);
                                            setPromoError("");
                                            if (appliedPromo) setAppliedPromo(null);
                                        }}
                                        placeholder="Enter code"
                                        autoComplete="off"
                                        spellCheck={false}
                                        disabled={busy}
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !busy) {
                                                e.preventDefault();
                                                applyPromo(selectedPlan, promoCode);
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="paywall-promo-apply"
                                        onClick={() => applyPromo(selectedPlan, promoCode)}
                                        disabled={busy || !promoCode.trim()}
                                    >
                                        {isValidatingPromo ? "..." : "Apply"}
                                    </button>
                                </div>
                                {promoError && (
                                    <p className="paywall-promo-error" role="alert">
                                        {promoError}
                                    </p>
                                )}
                            </div>
                        )}

                        {hasActivePromo && (
                            <div className="paywall-promo-applied" role="status" aria-live="polite">
                                <div className="paywall-promo-applied-header">
                                    <span className="paywall-promo-active-badge">
                                        <Check size={12} strokeWidth={3} aria-hidden="true" />
                                        Coupon active
                                    </span>
                                    <button
                                        type="button"
                                        className="paywall-promo-remove"
                                        onClick={clearPromo}
                                        disabled={busy}
                                    >
                                        Remove
                                    </button>
                                </div>
                                <p className="paywall-promo-applied-code">
                                    {appliedPromo.code} - {appliedPromo.discountLabel}
                                </p>
                                <div className="paywall-summary-item-line paywall-discount-line">
                                    <span>Discount</span>
                                    <span>{appliedPromo.discountAmount}</span>
                                </div>
                            </div>
                        )}

                        <div className="paywall-summary-total-row">
                            <span>Today&apos;s total</span>
                            <strong>{displayTotal}</strong>
                        </div>

                        <button
                            type="button"
                            className="paywall-cta"
                            onClick={() => onSubscribe(
                                selectedPlan,
                                hasActivePromo ? appliedPromo.code : promoCode.trim()
                            )}
                            disabled={busy}
                        >
                            {isCheckoutLoading ? "Loading..." : "Continue to checkout"}
                        </button>

                        <div className="paywall-secure">
                            <Lock size={14} aria-hidden="true" />
                            <span>Secure checkout</span>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
